import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { INFO_TOPICS, type InfoTopicId } from './infoTopics';
import './InfoPopover.css';

export interface InfoPopoverProps {
  /** Key into the help registry in `infoTopics.ts`. */
  readonly topic: InfoTopicId;
}

/** Grace period before a hover-opened card closes, so the pointer can cross the gap. */
const HOVER_CLOSE_DELAY_MS = 150;
const GAP_PX = 8;
const VIEWPORT_MARGIN_PX = 12;
const MAX_WIDTH_PX = 340;

/**
 * Circular ℹ trigger that opens a contextual help card.
 *
 * - Hover opens the card; leaving closes it unless it was pinned by a click.
 * - Click pins the card open; a second click closes it.
 * - Escape or the close button dismisses it and returns focus to the trigger.
 * - A pointer press outside the trigger and card dismisses it.
 *
 * The card is portalled to <body> so panels with `overflow: hidden` cannot clip it.
 */
export function InfoPopover({ topic }: InfoPopoverProps): ReactElement {
  const info = INFO_TOPICS[topic];
  const uid = useId();
  const titleId = `${uid}-title`;
  const popoverId = `${uid}-popover`;

  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);

  const cancelScheduledClose = useCallback((): void => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const close = useCallback(
    (restoreFocus: boolean): void => {
      cancelScheduledClose();
      setOpen(false);
      setPinned(false);
      if (restoreFocus) {
        triggerRef.current?.focus();
      }
    },
    [cancelScheduledClose],
  );

  const handlePointerEnter = (): void => {
    cancelScheduledClose();
    setOpen(true);
  };

  const handlePointerLeave = (): void => {
    if (pinned) return;
    cancelScheduledClose();
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;
      setOpen(false);
    }, HOVER_CLOSE_DELAY_MS);
  };

  const handleTriggerClick = (): void => {
    if (open && pinned) {
      close(false);
      return;
    }
    cancelScheduledClose();
    setOpen(true);
    setPinned(true);
  };

  // Clear any pending hover-close timer when the trigger unmounts.
  useEffect(() => cancelScheduledClose, [cancelScheduledClose]);

  // Escape dismisses; pointer presses outside both trigger and card dismiss.
  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close(true);
      }
    };

    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      close(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open, close]);

  // Position the card beside the trigger, flipping above when there is no room
  // below and clamping inside the viewport. Writes styles directly so repositioning
  // never triggers a React re-render.
  useLayoutEffect(() => {
    if (!open) return undefined;

    const place = (): void => {
      const trigger = triggerRef.current;
      const card = popoverRef.current;
      if (!trigger || !card) return;

      const anchor = trigger.getBoundingClientRect();
      const width = Math.min(MAX_WIDTH_PX, window.innerWidth - VIEWPORT_MARGIN_PX * 2);
      card.style.width = `${width}px`;

      const height = card.offsetHeight;
      const below = anchor.bottom + GAP_PX;
      const fitsBelow = below + height <= window.innerHeight - VIEWPORT_MARGIN_PX;
      const top = fitsBelow
        ? below
        : Math.max(VIEWPORT_MARGIN_PX, anchor.top - GAP_PX - height);
      const maxLeft = window.innerWidth - width - VIEWPORT_MARGIN_PX;
      const left = Math.min(Math.max(anchor.left, VIEWPORT_MARGIN_PX), maxLeft);

      card.style.top = `${top}px`;
      card.style.left = `${left}px`;
    };

    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="info-trigger"
        aria-label={`About ${info.title}`}
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        onClick={handleTriggerClick}
        onMouseEnter={handlePointerEnter}
        onMouseLeave={handlePointerLeave}
      >
        <span aria-hidden="true">ℹ</span>
      </button>

      {open &&
        createPortal(
          <div
            id={popoverId}
            ref={popoverRef}
            role="dialog"
            aria-labelledby={titleId}
            className="info-pop"
            onMouseEnter={handlePointerEnter}
            onMouseLeave={handlePointerLeave}
          >
            <header className="info-pop__head">
              <h3 id={titleId} className="info-pop__title">
                {info.title}
              </h3>
              <button
                type="button"
                className="info-pop__close"
                aria-label="Close help"
                onClick={() => close(true)}
              >
                ×
              </button>
            </header>

            <p className="info-pop__summary">{info.summary}</p>

            {info.formula !== undefined && (
              <section className="info-pop__section" aria-label="Formula">
                <h4 className="info-pop__label">Formula</h4>
                <pre className="info-pop__formula">{info.formula}</pre>
              </section>
            )}

            <section className="info-pop__section" aria-label="Guidance">
              <h4 className="info-pop__label">Strategy</h4>
              <ul className="info-pop__guidance">
                {info.guidance.map((tip) => (
                  <li key={tip}>{tip}</li>
                ))}
              </ul>
            </section>
          </div>,
          document.body,
        )}
    </>
  );
}
